import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ArrowLeft,
  Users,
  Clock,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  MapPin,
  ExternalLink,
  User,
  Type,
  Copy,
  Trash2
} from 'lucide-react';
import NameResolver from '../../components/ppc-farm/NameResolver';
import DuplicateResolver from '../../components/ppc-farm/DuplicateResolver';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  Timestamp
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../config/firebase';
import { usePageTitle } from '../../hooks/usePageTitle';

// Types for cohort and member data
interface CohortData {
  id: string;
  cohortNumber: number;
  createdByName: string;
  cohortNotes?: string;
  createdAt: Timestamp;
  status: 'IN_REVIEW' | 'APPROVED' | 'COMPLETED';
  stats: {
    totalSellers: number;
    problemsFound: number;
    problemsSolved: number;
  };
}

interface CohortMember {
  id: string;
  cohortId: string;
  sellerId: string;
  originalName: string;
  workingName: string;
  originalAddress: string;
  workingAddress: string;
  problemFlags: {
    isDuplicate?: 'NEEDS_REVIEW' | 'APPROVED' | 'DELETED';
    isLongName?: 'NEEDS_REVIEW' | 'APPROVED' | 'DELETED';
    isAllCaps?: 'NEEDS_REVIEW' | 'APPROVED' | 'DELETED';
    isAllSmall?: 'NEEDS_REVIEW' | 'APPROVED' | 'DELETED';
  };
  addressHelperData?: {
    addressType?: 'BUSINESS' | 'RESIDENTIAL' | 'UNKNOWN';
    latitude?: number;
    longitude?: number;
  };
}

interface ProblemGroup {
  type: 'isDuplicate' | 'isLongName' | 'isAllCaps' | 'isAllSmall';
  label: string;
  members: CohortMember[];
}

const ReviewSessionPage: React.FC = () => {
  const { cohortId } = useParams<{ cohortId: string }>();
  const navigate = useNavigate();
  
  const [cohort, setCohort] = useState<CohortData | null>(null);
  const [members, setMembers] = useState<CohortMember[]>([]);
  const [problemGroups, setProblemGroups] = useState<ProblemGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageTitle(cohort ? `Review Cohort ${cohort.cohortNumber} - PPC Farm` : 'Review Session - PPC Farm');

  // Load cohort data
  useEffect(() => {
    if (!cohortId) {
      setError('No cohort ID provided');
      setIsLoading(false);
      return;
    }

    const loadCohort = async () => {
      try {
        const cohortRef = doc(db, 'ppcFarm_cohorts', cohortId);
        const cohortSnap = await getDoc(cohortRef);
        
        if (!cohortSnap.exists()) {
          setError('Cohort not found');
          setIsLoading(false);
          return;
        }

        const cohortData = cohortSnap.data();
        setCohort({
          id: cohortSnap.id,
          cohortNumber: cohortData.cohortNumber,
          createdByName: cohortData.createdByName,
          cohortNotes: cohortData.cohortNotes,
          createdAt: cohortData.createdAt,
          status: cohortData.status,
          stats: {
            totalSellers: cohortData.stats?.totalSellers || 0,
            problemsFound: cohortData.stats?.problemsFound || 0,
            problemsSolved: cohortData.stats?.problemsSolved || 0,
          },
        });
      } catch (err) {
        console.error('❌ Error loading cohort:', err);
        setError('Failed to load cohort data');
        setIsLoading(false);
      }
    };

    loadCohort();
  }, [cohortId]);

  // Real-time subscription to cohort members
  useEffect(() => {
    if (!cohortId) return;

    console.log('🔄 Setting up real-time subscription for cohort members...');
    
    const membersQuery = query(
      collection(db, 'ppcFarm_cohort_members'),
      where('cohortId', '==', cohortId)
    );

    const unsubscribe = onSnapshot(
      membersQuery,
      (snapshot) => {
        try {
          const memberData: CohortMember[] = [];
          
          snapshot.forEach((doc) => {
            const data = doc.data();
            memberData.push({
              id: doc.id,
              cohortId: data.cohortId,
              sellerId: data.sellerId,
              originalName: data.originalName,
              workingName: data.workingName,
              originalAddress: data.originalAddress,
              workingAddress: data.workingAddress,
              problemFlags: data.problemFlags || {},
              addressHelperData: data.addressHelperData || {},
            });
          });

          console.log(`📊 Loaded ${memberData.length} cohort members`);
          setMembers(memberData);
          
          // Group members by problem type
          groupMembersByProblems(memberData);
          setError(null);
        } catch (err) {
          console.error('❌ Error processing members data:', err);
          setError('Failed to process members data');
        } finally {
          setIsLoading(false);
        }
      },
      (err) => {
        console.error('❌ Error in members subscription:', err);
        setError('Failed to load members. Please try again.');
        setIsLoading(false);
      }
    );

    return () => {
      console.log('🔄 Cleaning up members subscription');
      unsubscribe();
    };
  }, [cohortId]);

  // Group members by problem types
  const groupMembersByProblems = (memberData: CohortMember[]) => {
    const groups: ProblemGroup[] = [
      { type: 'isDuplicate', label: 'Duplicate Addresses', members: [] },
      { type: 'isLongName', label: 'Long Names', members: [] },
      { type: 'isAllCaps', label: 'All Caps Names', members: [] },
      { type: 'isAllSmall', label: 'All Small Names', members: [] },
    ];

    // Filter members that have problems needing review
    const problemMembers = memberData.filter(member => {
      return Object.values(member.problemFlags).some(flag => flag === 'NEEDS_REVIEW');
    });

    // Group by problem type
    problemMembers.forEach(member => {
      Object.entries(member.problemFlags).forEach(([problemType, status]) => {
        if (status === 'NEEDS_REVIEW') {
          const group = groups.find(g => g.type === problemType);
          if (group) {
            group.members.push(member);
          }
        }
      });
    });

    // Only include groups that have members
    const nonEmptyGroups = groups.filter(group => group.members.length > 0);
    setProblemGroups(nonEmptyGroups);
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp: Timestamp): string => {
    const date = timestamp.toDate();
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
  };

  // Calculate progress percentage
  const calculateProgress = (problemsSolved: number, problemsFound: number): number => {
    if (problemsFound === 0) return 100;
    return Math.round((problemsSolved / problemsFound) * 100);
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/ppc-farm/potential-problems');
  };

  // Handle problem resolution
  const handleProblemResolved = async (
    cohortMemberId: string,
    problemToFix: string,
    action: 'APPROVE' | 'DELETE',
    updatedWorkingName?: string
  ) => {
    try {
      console.log('🔧 Resolving problem:', { cohortMemberId, problemToFix, action, updatedWorkingName });
      
      const functions = getFunctions();
      const resolveProblem = httpsCallable(functions, 'resolveProblem');
      
      const result = await resolveProblem({
        cohortMemberId,
        problemToFix,
        action,
        updatedWorkingName
      });
      
      console.log('✅ Problem resolved successfully:', result.data);
      
      // The real-time subscription will automatically update the UI
      // No need to manually refresh data
      
    } catch (error) {
      console.error('❌ Error resolving problem:', error);
      // In a production app, you'd want to show a toast notification here
      throw error; // Re-throw so the component can handle it
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        <div className="flex items-center mb-6">
          <Button variant="ghost" onClick={handleBack} className="mr-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Problems
          </Button>
        </div>

        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
          <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        <div className="flex items-center mb-6">
          <Button variant="ghost" onClick={handleBack} className="mr-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Problems
          </Button>
        </div>

        <Card className="border-red-200 dark:border-red-800">
          <CardContent className="p-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error Loading Review Session
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error}
                </p>
              </div>
            </div>
            <Button 
              onClick={() => window.location.reload()} 
              variant="outline" 
              size="sm" 
              className="mt-4"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!cohort) {
    return null;
  }

  const progress = calculateProgress(cohort.stats.problemsSolved, cohort.stats.problemsFound);
  const isComplete = problemGroups.length === 0;

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-6">
        <Button variant="ghost" onClick={handleBack} className="mr-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Problems
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            Review Session: Cohort {cohort.cohortNumber}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Created by {cohort.createdByName} • {formatTimestamp(cohort.createdAt)}
          </p>
        </div>
        <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 dark:bg-yellow-900/10 dark:text-yellow-400">
          {cohort.status}
        </Badge>
      </div>

      {/* Cohort Notes */}
      {cohort.cohortNotes && (
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-start">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg mr-3">
                <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-1">Cohort Notes</h3>
                <p className="text-gray-700 dark:text-gray-300">"{cohort.cohortNotes}"</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sellers</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {cohort.stats.totalSellers.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Problems Found</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {cohort.stats.problemsFound.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Problems Solved</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {cohort.stats.problemsSolved.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Progress</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {progress}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress Bar */}
      <Card className="mb-8">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Overall Progress
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {cohort.stats.problemsSolved} / {cohort.stats.problemsFound} problems solved
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div 
              className="bg-yellow-500 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      {isComplete ? (
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              All Problems Solved!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Congratulations! All problems in this cohort have been resolved. 
              The cohort is ready for the next phase.
            </p>
            <Button 
              onClick={handleBack}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
              Problem Resolution Workspace
            </CardTitle>
            <p className="text-gray-600 dark:text-gray-400">
              Review and resolve address validation problems below. Each tab contains sellers with specific issues.
            </p>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={problemGroups[0]?.type || 'none'} className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                {problemGroups.map((group) => (
                  <TabsTrigger key={group.type} value={group.type} className="relative">
                    {group.label}
                    <Badge variant="secondary" className="ml-2 bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                      {group.members.length}
                    </Badge>
                  </TabsTrigger>
                ))}
              </TabsList>

              {problemGroups.map((group) => (
                <TabsContent key={group.type} value={group.type} className="mt-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {group.label} ({group.members.length} sellers)
                      </h3>
                    </div>

                    {/* Render appropriate resolver component based on problem type */}
                    {group.type === 'isDuplicate' ? (
                      <DuplicateResolver
                        members={group.members}
                        onResolveProblem={handleProblemResolved}
                      />
                    ) : (
                      <NameResolver
                        members={group.members}
                        problemType={group.type}
                        onResolveProblem={handleProblemResolved}
                      />
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReviewSessionPage;